import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from "react";
import { css } from "../../../../styled-system/css";
import { contentWrapper } from "../../../styles/contentWrapper";
import { pageTitle, titleWrapper } from "../../../styles/sectionStyles";
import { input, inputLabel, inputWrapper, radio, validationMessageLabel } from "../../../styles/inputStyles";
import KeyboardArrowDown from "~icons/material-symbols/keyboard-arrow-down.svg";
import { button } from "../../../styles/buttonStyles";
import { useDownloadZengin } from "../../../libs/admin/hooks";
import {
  type ZenginClosingDay,
  ZenginDownloadError,
} from "../../../libs/admin/zengin";
import { getTenantSlug } from "../../../libs/utils";

export const Route = createFileRoute('/admin/settlements/')({
  component: RouteComponent,
})

export function RouteComponent() {
  const isKyoto = getTenantSlug() === "kyoto-kyoto";
  const defaultYearMonth = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}${String(now.getMonth() + 1)}`;
  }, []);
  const now = useMemo(() => new Date(), []);
  const years = useMemo(() => {
    const currentYear = now.getFullYear();
    const startYear = 2024;
    return Array.from({ length: currentYear - startYear + 1 }, (_, i) => String(startYear + i));
  }, [now]);
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => String(i + 1)), []);
  const defaultYear = defaultYearMonth.slice(0, 4);
  const defaultMonth = defaultYearMonth.slice(4, 6);
  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [acceptType, setAcceptType] = useState<"text/zengin" | "text/csv">("text/zengin");
  const [closingDay, setClosingDay] = useState<ZenginClosingDay>("15");
  const {
    mutate: downloadZengin,
    isPending: isDownloading,
    error: downloadError,
    reset: resetDownload,
  } = useDownloadZengin();

  const handleDownload = () => {
    resetDownload();
    const yearMonth = `${year}${month.padStart(2, "0")}`;
    const params = isKyoto
      ? { yearMonth, closingDay, accept: acceptType }
      : { yearMonth, accept: acceptType };
    downloadZengin(params, {
      onSuccess: ({ blob, fileName }) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      },
      onError: (err) => {
        console.error("Failed to download zengin file", err);
      },
    });
  };

  const handleShiftMonth = (delta: number) => {
    const current = new Date(Number(year), Number(month) - 1, 1);
    current.setMonth(current.getMonth() + delta);

    const newYear = current.getFullYear();
    const newMonth = current.getMonth() + 1;
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    if (newYear < 2024) return;
    if (newYear > currentYear) return;
    if (newYear === currentYear && newMonth > currentMonth) return;

    setYear(String(newYear));
    setMonth(String(newMonth));
  };

  const canShiftMonth = (delta: number) => {
    const current = new Date(Number(year), Number(month) - 1, 1);
    current.setMonth(current.getMonth() + delta);
    const newYear = current.getFullYear();
    const newMonth = current.getMonth() + 1;
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    if (newYear < 2024) return false;
    if (newYear > currentYear) return false;
    if (newYear === currentYear && newMonth > currentMonth) return false;
    return true;
  };

  const isInvalidYearMonth = !year || !month;

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>支払い処理用データ</h1>
      </div>
      <div className={css({
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      })}>
        <div className={css({
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          maxWidth: "480px",
        })}>
          <div className={inputWrapper()}>
            <p>
              {isKyoto
                ? "締め対象月と締め日を指定して全銀ファイルのデータをダウンロードできます。"
                : "月を指定して全銀ファイルのデータをダウンロードできます。"}
            </p>
          </div>
          <div className={inputWrapper()}>
            <div className={inputLabel()}>ダウンロードファイル形式</div>
            <div className={css({
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              flexDirection: "column",
            })}>
              {[
                { value: "text/zengin", label: "全銀ファイル" },
                { value: "text/csv", label: "CSV" },
              ].map((option) => (
                <label
                  key={option.value}
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  })}
                >
                  <input
                    type="radio"
                    name="acceptType"
                    value={option.value}
                    checked={acceptType === option.value}
                    onChange={() => setAcceptType(option.value as "text/zengin" | "text/csv")}
                    className={radio()}
                  />
                  <span className={css({ fontSize: "14px", color: "text.primary" })}>
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
          {isKyoto && (
            <div className={inputWrapper()}>
              <div className={inputLabel()}>締め日</div>
              <div className={css({
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
                flexDirection: "column",
              })}>
                {[
                  { value: "15", label: "15日締め" },
                  { value: "month-end", label: "月末締め" },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={css({
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                    })}
                  >
                    <input
                      type="radio"
                      name="closingDay"
                      value={option.value}
                      checked={closingDay === option.value}
                      onChange={() => setClosingDay(option.value as ZenginClosingDay)}
                      className={radio()}
                    />
                    <span className={css({ fontSize: "14px", color: "text.primary" })}>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
              <p className={css({ fontSize: "12px", color: "text.secondary" })}>
                選択した締め日の23:59:59までに発生した取引が対象です。
              </p>
            </div>
          )}
          <div className={inputWrapper()}>
            <div className={inputLabel()}>
              {isKyoto ? "締め対象月" : "対象期間"}
            </div>
            <div className={css({
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            })}>
              <div className={css({ position: "relative", width: "120px" })}>
                <select
                  aria-label="年"
                  className={css(input.raw(), { appearance: "none" })}
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                >
                  {years.map((optionYear) => (
                    <option key={optionYear} value={optionYear}>
                      {optionYear}
                    </option>
                  ))}
                </select>
                <KeyboardArrowDown className={css({
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  color: "text.secondary",
                })} />
              </div>
              <span className={css({ fontSize: "14px", color: "text.secondary" })}>年</span>
              <div className={css({ position: "relative", width: "100px" })}>
                <select
                  aria-label="月"
                  className={css(input.raw(), { appearance: "none" })}
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                >
                  {months.map((optionMonth) => (
                    <option key={optionMonth} value={optionMonth}>
                      {optionMonth}
                    </option>
                  ))}
                </select>
                <KeyboardArrowDown className={css({
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  color: "text.secondary",
                })} />
              </div>
              <span className={css({ fontSize: "14px", color: "text.secondary" })}>月</span>
              <div className={css({
                display: "flex",
                alignItems: "center",
                gap: "0px",
              })}>
                <button
                  type="button"
                  className={css(button.raw(), {
                    borderRadius: "8px 0 0 8px",
                    height: "36px",
                  })}
                  onClick={() => handleShiftMonth(-1)}
                  disabled={!canShiftMonth(-1)}
                >
                  前月
                </button>
                <button
                  type="button"
                  className={css(button.raw(), {
                    borderRadius: "0 8px 8px 0",
                    borderLeftWidth: "0px",
                    height: "36px",
                  })}
                  onClick={() => handleShiftMonth(1)}
                  disabled={!canShiftMonth(1)}
                >
                  次月
                </button>
              </div>
            </div>
          </div>
          <div className={css({
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          })}>
            <button
              type="button"
              className={button({ color: "primary" })}
              onClick={handleDownload}
              disabled={isDownloading || isInvalidYearMonth}
            >
              {isDownloading ? "ダウンロード中..." : "全銀ファイルをダウンロード"}
            </button>
            {downloadError && (
              <div className={validationMessageLabel({ semantic: "invalid" })}>
                {downloadError instanceof ZenginDownloadError &&
                downloadError.status === 404
                  ? "指定した期間のファイルが見つかりません。"
                  : "ダウンロードに失敗しました。システム管理者にお問い合わせください。"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
